/**
 * Feb 2026 v23 — Fallback con verificación server-side + resolución PROACTIVA.
 *
 * Historia:
 *   v20: fallback client-side directo → sirvió dorsos.
 *   v21: HEAD client-side → CORS asimétrico rompió.
 *   v22: verificación server-side vía `/api/img/verify-fallback` → funciona
 *        pero solo se dispara cuando ya hay un <img> con URL TCGdex fallando.
 *   v23: agregamos resolución PROACTIVA para cards que NO tienen image_url
 *        (image_url=null en db.cards). Se calcula desde set_id + number
 *        antes de renderizar.
 *
 * Reglas:
 *   - NUNCA mostrar el dorso al usuario. Preferimos "Sin imagen".
 *   - Cero cambios en db.cards, buscador, ni backend salvo `/img/verify-fallback`.
 *   - Reversible: cuando TCGdex vuelva, el fallback deja de dispararse solo.
 */

const TCGDEX_URL_REGEX = /https?:\/\/assets\.tcgdex\.net\/[a-z]{2}\/(?:[^/]+\/)?([^/]+)\/([^/]+)\/[^/]+$/i;

// Cache in-memory: {"setId|localId": Promise<string|null>}
const _cache = new Map();

/**
 * Extrae {setId, localId} de una URL TCGdex.
 */
export function parseTcgdexUrl(tcgdexUrl) {
  if (!tcgdexUrl || typeof tcgdexUrl !== "string") return null;
  if (!tcgdexUrl.includes("assets.tcgdex.net")) return null;
  const match = tcgdexUrl.match(TCGDEX_URL_REGEX);
  if (!match) return null;
  const [, setId, localId] = match;
  return { setId, localId };
}

/**
 * Consulta backend para obtener la URL real y verificada de pokemontcg.io.
 * Cache por par (setId, localId).
 */
export async function resolveFromSetLocal(setId, localId) {
  if (!setId || !localId) return null;
  const key = `${setId}|${localId}`;
  if (_cache.has(key)) return _cache.get(key);
  const promise = (async () => {
    try {
      const params = new URLSearchParams({ set_id: String(setId), local_id: String(localId) });
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backendUrl}/api/img/verify-fallback?${params}`);
      if (!res.ok) return null;
      const data = await res.json();
      return data && data.image_url ? data.image_url : null;
    } catch (e) {
      return null;
    }
  })();
  _cache.set(key, promise);
  return promise;
}

/**
 * Resuelve desde URL TCGdex (parsea y llama al backend).
 */
export async function resolvePokemontcgFallback(tcgdexUrl) {
  const parsed = parseTcgdexUrl(tcgdexUrl);
  if (!parsed) return null;
  return resolveFromSetLocal(parsed.setId, parsed.localId);
}

/**
 * Handler onError para <img> de cartas.
 */
export function makeCardImgFallback(onFinalFail) {
  return (e) => {
    const img = e.currentTarget;
    if (!img) return;
    if (img.dataset.fallbackTried) {
      if (typeof onFinalFail === "function") onFinalFail(img, e);
      else img.style.display = "none";
      return;
    }
    img.dataset.fallbackTried = "trying";
    resolvePokemontcgFallback(img.src)
      .then((newUrl) => {
        img.dataset.fallbackTried = "done";
        if (newUrl && newUrl !== img.src) {
          img.src = newUrl;
        } else if (typeof onFinalFail === "function") {
          onFinalFail(img, e);
        } else {
          img.style.display = "none";
        }
      })
      .catch(() => {
        img.dataset.fallbackTried = "done";
        if (typeof onFinalFail === "function") onFinalFail(img, e);
        else img.style.display = "none";
      });
  };
}

/**
 * Hook React: resuelve proactivamente URLs de imagen para una lista de cards.
 * Devuelve `{cardId: resolvedUrl}` — solo entradas donde encontramos imagen.
 *
 * Uso:
 *   const resolved = useProactiveResolve(variants, (v) => v.card);
 *   const imgSrc = originalUrl || resolved[v.card.id];
 *
 * `getCard(item)` debe retornar el objeto card con id, image_url, set_id/set.id, number/local_id.
 * Solo dispara requests para cards SIN `image_url`. Cachea globalmente en `_cache`.
 */
import { useState, useEffect, useRef } from "react";

export function useProactiveResolve(items, getCard) {
  const [resolved, setResolved] = useState({});
  const seen = useRef(new Set());

  useEffect(() => {
    if (!items || items.length === 0) return;
    const toResolve = [];
    for (const it of items) {
      const card = getCard(it);
      if (!card || !card.id) continue;
      if (card.image_url) continue; // ya tiene imagen, no hay nada que resolver
      const setId = card.set_id || card.set?.id || null;
      const localId = card.number || card.local_id || null;
      if (!setId || !localId) continue;
      if (seen.current.has(card.id)) continue;
      seen.current.add(card.id);
      toResolve.push({ cardId: card.id, setId, localId });
    }
    if (toResolve.length === 0) return;
    let cancelled = false;
    Promise.all(
      toResolve.map(async ({ cardId, setId, localId }) => {
        const url = await resolveFromSetLocal(setId, localId);
        return [cardId, url];
      })
    ).then((pairs) => {
      if (cancelled) return;
      const patch = {};
      let hadHit = false;
      for (const [id, url] of pairs) {
        if (url) {
          patch[id] = url;
          hadHit = true;
        }
      }
      if (hadHit) setResolved((prev) => ({ ...prev, ...patch }));
    });
    return () => {
      cancelled = true;
    };
  }, [items, getCard]);

  return resolved;
}
